import axios from 'axios';
import type {
  CommonCodeGroupResponse,
  CommonCodeValueResponse,
  CommonCodeGroupRequest,
  CommonCodeValueRequest,
  CodeGroupSearchCondition,
  CodeItemSearchCondition,
  PageResponse,
} from '../types/code';
import { getStoredToken } from '../../login/api/loginApi';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

// Request interceptor — attach Bearer token
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — unwrap ApiResponse<T>
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? 'Unknown error',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      'Network error';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

export const codeApi = {
  /** GET /api/eraf/codes/groups — 그룹 목록 (페이징/검색/필터/정렬) */
  listGroups: (params: CodeGroupSearchCondition): Promise<PageResponse<CommonCodeGroupResponse>> => {
    const searchParams = new URLSearchParams();
    if (params.keyword) searchParams.set('keyword', params.keyword);
    if (params.systemManaged !== undefined) searchParams.set('systemManaged', String(params.systemManaged));
    if (params.page !== undefined) searchParams.set('page', String(params.page));
    if (params.size !== undefined) searchParams.set('size', String(params.size));
    if (params.sort) searchParams.set('sort', params.sort);

    return apiClient
      .get<unknown, { data: PageResponse<CommonCodeGroupResponse> }>(
        `/codes/groups?${searchParams.toString()}`
      )
      .then((res) => res.data);
  },

  /** GET /api/eraf/codes/groups/{groupCode} — 그룹 상세 */
  getGroup: (groupCode: string): Promise<CommonCodeGroupResponse> =>
    apiClient
      .get<unknown, { data: CommonCodeGroupResponse }>(`/codes/groups/${groupCode}`)
      .then((res) => res.data),

  /** POST /api/eraf/codes/groups — 신규 그룹 생성 */
  createGroup: (body: CommonCodeGroupRequest): Promise<CommonCodeGroupResponse> =>
    apiClient
      .post<unknown, { data: CommonCodeGroupResponse }>('/codes/groups', body)
      .then((res) => res.data),

  /** PATCH /api/eraf/codes/groups/{groupCode} — 그룹 수정 */
  updateGroup: (groupCode: string, body: Partial<CommonCodeGroupRequest>): Promise<CommonCodeGroupResponse> =>
    apiClient
      .patch<unknown, { data: CommonCodeGroupResponse }>(`/codes/groups/${groupCode}`, body)
      .then((res) => res.data),

  /** DELETE /api/eraf/codes/groups/{groupCode} — 그룹 삭제 */
  deleteGroup: (groupCode: string): Promise<void> =>
    apiClient.delete(`/codes/groups/${groupCode}`).then(() => undefined),

  /** GET /api/eraf/codes/groups/{groupCode}/items — 항목 목록 */
  listItems: (groupCode: string, params?: CodeItemSearchCondition): Promise<CommonCodeValueResponse[]> => {
    const searchParams = new URLSearchParams();
    if (params?.useYn !== undefined) searchParams.set('useYn', String(params.useYn));
    if (params?.locale) searchParams.set('locale', params.locale);
    const qs = searchParams.toString();

    return apiClient
      .get<unknown, { data: CommonCodeValueResponse[] }>(
        `/codes/groups/${groupCode}/items${qs ? `?${qs}` : ''}`
      )
      .then((res) => res.data);
  },

  /** POST /api/eraf/codes/groups/{groupCode}/items — 항목 신규 생성 */
  createItem: (groupCode: string, body: CommonCodeValueRequest): Promise<CommonCodeValueResponse> =>
    apiClient
      .post<unknown, { data: CommonCodeValueResponse }>(`/codes/groups/${groupCode}/items`, body)
      .then((res) => res.data),

  /** PATCH /api/eraf/codes/groups/{groupCode}/items/{itemCode} — 항목 수정 */
  updateItem: (
    groupCode: string,
    itemCode: string,
    body: Partial<CommonCodeValueRequest>
  ): Promise<CommonCodeValueResponse> =>
    apiClient
      .patch<unknown, { data: CommonCodeValueResponse }>(
        `/codes/groups/${groupCode}/items/${itemCode}`,
        body
      )
      .then((res) => res.data),

  /** DELETE /api/eraf/codes/groups/{groupCode}/items/{itemCode} — 항목 삭제 */
  deleteItem: (groupCode: string, itemCode: string): Promise<void> =>
    apiClient.delete(`/codes/groups/${groupCode}/items/${itemCode}`).then(() => undefined),
};
